import React from 'react';
import { useMockExam } from '../../hooks/useMockExam';
import MockExamLobby from './MockExamLobby';
import MockExamWizard from './MockExamWizard';
import MockExamProcessing from './MockExamProcessing';
import MockExamReveal from './MockExamReveal';

export default function MockExamOrchestrator({ onClose, getToken, subject = 'geography' }) {
  const { state, dispatch, remainingSeconds } = useMockExam(getToken);

  const handleStart = (geographyType, mockType, group, paper) => {
    dispatch({ type: 'START_EXAM', timestamp: Date.now(), geographyType, mockType, group, paper, subject });
  };

  const handleAnswerChange = (questionId, cardLabel, value) => {
    dispatch({ type: 'UPDATE_ANSWER', questionId, cardLabel, value });
  };

  const handleNavigate = (index, timestamp) => {
    dispatch({ type: 'NAVIGATE', index, timestamp });
  };

  const handleSubmit = (timestamp) => {
    dispatch({ type: 'SUBMIT_EXAM', timestamp });
  };

  const handleExit = () => {
    dispatch({ type: 'EXIT_EXAM' });
    localStorage.removeItem('mockExamInProgress');
    onClose();
  };

  const renderPhase = () => {
    switch (state.phase) {
      case 'lobby':
        return (
          <MockExamLobby
            onStart={handleStart}
            onClose={onClose}
            subject={subject}
            getToken={getToken}
            prepareError={state.prepareError}
          />
        );

      case 'preparing':
        return (
          <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center">
            <div className="text-[10px] font-mono text-[#8b949e] tracking-widest uppercase mb-2">
              Preparing your paper
            </div>
            <div className="text-[#e6edf3] text-sm">Picking questions tailored to your history…</div>
            <div className="mt-6 w-8 h-8 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
          </div>
        );

      case 'active':
        return (
          <MockExamWizard
            questions={state.questions}
            currentIndex={state.currentIndex}
            answers={state.answers}
            remainingSeconds={remainingSeconds}
            onAnswerChange={handleAnswerChange}
            onNavigate={handleNavigate}
            onSubmit={handleSubmit}
            onCancel={handleExit}
          />
        );

      case 'processing':
        if (state.revealedUpTo >= 0) {
          return (
            <MockExamReveal
              questions={state.questions}
              results={state.results}
              answers={state.answers}
              revealedUpTo={state.revealedUpTo}
              onExit={handleExit}
            />
          );
        }
        return <MockExamProcessing results={state.results} questionCount={state.questions.length} />;

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/80">
      <div className="relative w-full max-w-xl h-[90vh] mx-4 flex flex-col bg-[#0d1117] border border-[#30363d] rounded-2xl shadow-2xl overflow-hidden">
        {/* Close button — visible in lobby and when exam errored */}
        {(state.phase === 'lobby' || state.submitError) && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center rounded-full text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#30363d] transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        )}

        {/* Submit error banner */}
        {state.submitError && (
          <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 bg-[#f85149]/10 border-b border-[#f85149]/30">
            <span className="text-[#f85149] text-xs font-mono">{state.submitError}</span>
            <button
              onClick={() => handleSubmit(Date.now())}
              className="ml-auto text-[#f85149] text-xs font-mono underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {renderPhase()}
      </div>
    </div>
  );
}
